import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { MessageCircle, X } from "lucide-react";
import api from "@/lib/api";
import { buildWhatsAppUrl } from "@/lib/whatsapp";

/**
 * Persistent floating WhatsApp bubble.
 * Opens a compact menu that lists all Adelina recruitment contacts so a
 * prospective candidate can chat with the right team in one tap.
 * Contacts come from `/api/settings/public` (managed by the CMS).
 */
export default function FloatingWhatsApp() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const { data: pub } = useQuery({
    queryKey: ["settings-public-fab"],
    queryFn: async () => (await api.get("/settings/public")).data,
  });
  const contacts = pub?.contacts || [];
  const lang = i18n.language || "en";
  const message = t("join.contact_default_message", "Hello Adelina, I would like to register as a candidate.");

  const link = (phone) => buildWhatsAppUrl(phone, message);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-24 sm:bottom-6 right-6 z-40 flex items-center gap-3 pl-4 pr-5 py-3 rounded-full bg-emerald-500 hover:bg-emerald-600 text-white shadow-2xl shadow-emerald-500/30 transition-all hover:scale-105"
        aria-label="Open WhatsApp menu"
        data-testid="fab-whatsapp"
      >
        {open ? <X size={20} /> : <MessageCircle size={20} />}
        <span className="text-sm font-semibold hidden sm:inline">
          {open ? t("action.cancel", "Close") : t("action.contact_wa", "Chat WhatsApp")}
        </span>
      </button>

      {open && contacts.length > 0 && (
        <div className="fixed bottom-24 right-6 z-40 w-[92vw] max-w-sm bg-white border border-zinc-200 rounded-3xl shadow-2xl overflow-hidden animate-in fade-in slide-in-from-bottom-4" data-testid="fab-whatsapp-menu">
          <div className="bg-emerald-500 text-white px-5 py-4">
            <div className="text-[10px] uppercase tracking-widest opacity-80">{t("brand.short", "Adelina")}</div>
            <div className="font-serif text-lg leading-snug">{t("join.contacts_title", "Choose the right team for you")}</div>
          </div>
          <div className="max-h-[60vh] overflow-y-auto p-2">
            {contacts.map((c) => (
              <a
                key={c.key}
                href={link(c.phone)}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-3 p-3 rounded-2xl hover:bg-zinc-50 transition-colors"
                data-testid={`fab-contact-${c.key}`}
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="text-emerald-600" size={18} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-semibold text-zinc-900 truncate">
                    {lang === "id" ? c.label_id : c.label_en}
                  </div>
                  <div className="text-xs font-mono text-zinc-800">{c.phone}</div>
                </div>
                <div className="text-[10px] uppercase tracking-widest text-emerald-600">{t("action.chat", "Chat")}</div>
              </a>
            ))}
          </div>
          <div className="px-5 py-3 bg-zinc-50 border-t border-zinc-100 text-xs text-zinc-800 flex items-center justify-between">
            <span>
              <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 mr-2 animate-pulse" />
              {t("common.online", "Online")}
            </span>
            <a href="/join" className="underline text-emerald-600 font-semibold">{t("join.full_options", "Full options →")}</a>
          </div>
        </div>
      )}
    </>
  );
}
